import type { IdeaStop, IdeaStopType } from '../types/ideaStop.types';
import { formatDateToLocal } from './dateUtils';

const parseIso = (value: unknown): Date | null => {
  if (value == null) return null;
  if (typeof value !== 'string' || !value.trim()) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Start / end instants for one idea stop (API shape). */
export const getIdeaStopBounds = (stop: IdeaStop): { start: Date | null; end: Date | null } => {
  const t = stop.stop_type as IdeaStopType;
  let start: Date | null = null;
  let end: Date | null = null;

  switch (t) {
    case 'Flight':
      start = parseIso(stop.departure_date_time) ?? parseIso(stop.start_date_time);
      end = parseIso(stop.arrival_date_time) ?? parseIso(stop.departure_date_time) ?? start;
      break;
    case 'Hotel':
      start = parseIso(stop.check_in_date_time) ?? parseIso(stop.start_date_time);
      end = parseIso(stop.check_out_date_time) ?? parseIso(stop.check_in_date_time) ?? start;
      break;
    case 'Ferry':
    case 'Cruise':
    case 'Rail':
    case 'Transportation':
      start = parseIso(stop.departure_date_time) ?? parseIso(stop.start_date_time);
      end = parseIso(stop.arrival_date_time) ?? parseIso(stop.departure_date_time) ?? start;
      break;
    case 'Activity':
    case 'CarRental':
    case 'Concert':
    case 'Theatre':
    case 'Meeting':
    case 'Shopping':
    case 'Restaurant':
    case 'Tour':
    case 'Parking':
    case 'Directions':
      start = parseIso(stop.start_date_time);
      end = parseIso(stop.end_date_time) ?? start;
      break;
    case 'BorderCrossing':
      start = parseIso(stop.start_date_time);
      end = start;
      break;
    default:
      start = parseIso(stop.start_date_time);
      end = parseIso(stop.end_date_time) ?? start;
  }

  return { start, end };
};

const spanFromAllStops = (stops: IdeaStop[]): { start: Date; end: Date } | null => {
  const times: number[] = [];
  for (const s of stops) {
    const { start, end } = getIdeaStopBounds(s);
    if (start) times.push(start.getTime());
    if (end) times.push(end.getTime());
  }
  if (!times.length) return null;
  return { start: new Date(Math.min(...times)), end: new Date(Math.max(...times)) };
};

/** Stops as returned on GET /ideas/:id/ — use start_date_time / end_date_time only. */
export const getTripDateRangeFromEmbedStops = (
  stops: Array<{ start_date_time?: string | null; end_date_time?: string | null }>
): { startYmd: string; endYmd: string } | null => {
  if (!stops.length) return null;
  const times: number[] = [];
  for (const s of stops) {
    const a = parseIso(s.start_date_time);
    const b = parseIso(s.end_date_time);
    if (a) times.push(a.getTime());
    if (b) times.push(b.getTime());
  }
  if (!times.length) return null;
  const start = new Date(Math.min(...times));
  const end = new Date(Math.max(...times));
  if (start.getTime() > end.getTime()) {
    return { startYmd: formatDateToLocal(start), endYmd: formatDateToLocal(start) };
  }
  return { startYmd: formatDateToLocal(start), endYmd: formatDateToLocal(end) };
};

export const getTripDateRangeFromIdeaStops = (stops: IdeaStop[]): { startYmd: string; endYmd: string } | null => {
  if (!stops.length) return null;
  const span = spanFromAllStops(stops);
  if (!span) return null;
  if (span.start.getTime() > span.end.getTime()) {
    return { startYmd: formatDateToLocal(span.start), endYmd: formatDateToLocal(span.start) };
  }
  return { startYmd: formatDateToLocal(span.start), endYmd: formatDateToLocal(span.end) };
};
